import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Container } from '@material-ui/core';
import { get } from 'lodash';
import Form from '../../../components/RxForm/Form';
import RxFormsApiService from '../../../services/api/rxForms';
import LoadAvailableLocations from '../../../components/LoadAvailableLocations';
import LoadingButton from '../../../components/LoadingButton';
import { overridePreferenceWithModificationRules } from '../../../components/RxForm/IDB/helpers';
import withOrganizationPricing from '../../../hooks/withOrganizationPricing';
import {
  CONTROL_EXISTING,
  CONTROL_FILE
} from '../../Account/components/RxFormCustomization/FormBuilder/helpers';
import createIdbNativeFields from '../../../components/RxForm/IDB/nativeFields';
import { createIdbValidationSchema } from '../../../components/RxForm/IDB/validation';
import { getCustomFieldOverride } from '../../../components/RxForm/IDB/customFieldOverrides';

const UpdateIdbRxForm = ({
  rxForm,
  preference,
  afterSave,
  onSaveFailure,
  onCancel,
  modificationRules,
  hiddenFields,
  thirdPartyPartnerName
}) => {
  const patientDetails = `Patient: ${rxForm.last_name}, ${rxForm.first_name} - ${rxForm.organization.name}`;
  const onlyAttributes = [
    ...Object.keys(modificationRules),
    ...Object.keys(modificationRules?.custom_fields?.fields || [])
  ];
  const [saving, setSaving] = useState(false);

  let formStructure = null;
  let newControls = null;
  const data = {
    ...rxForm,
    ...rxForm.custom_field_values
  };
  if (rxForm.form_builder_data) {
    const formBuilderForm = rxForm.form_builder_data.form;
    formStructure = rxForm.form_builder_data.layout;
    newControls = Object.values(get(formBuilderForm, 'controls', {})).filter(
      control => control.system_type !== CONTROL_EXISTING && control.system_type !== CONTROL_FILE
    );
  }
  if (rxForm.arch_wires && rxForm.arch_wires.length) {
    data.chosen_arch_wires = [...rxForm.arch_wires];
    data.arch_wires = rxForm.arch_wires.map(archWire => archWire.id);
  }

  if (rxForm.bracket_libraries && rxForm.bracket_libraries.length) {
    data.chosen_bracket_libraries = [...rxForm.bracket_libraries];
    data.bracket_libraries = rxForm.bracket_libraries.map(bracketLibrary => bracketLibrary.id);
  }

  const modifiedPreferences = overridePreferenceWithModificationRules(
    preference,
    modificationRules,
    rxForm
  );

  const save = values => {
    const payload = { ...values };
    delete payload.chosen_arch_wires;
    const api = new RxFormsApiService();
    setSaving(true);
    api
      .patch(payload.id, payload)
      .then(() => {
        setSaving(false);
        afterSave();
      })
      .catch(error => {
        setSaving(false);
        onSaveFailure(error);
      });
  };

  const children = pricing => {
    return (
      <Container maxWidth="md">
        <LoadAvailableLocations organization={rxForm.organization}>
          {availableLocations => (
            <Form
              organization={rxForm.organization}
              title="Modify RxForm"
              description={patientDetails}
              preference={modifiedPreferences}
              onSubmit={save}
              rxForm={data}
              isLoading={false}
              only={onlyAttributes}
              formStructure={formStructure}
              newControls={newControls}
              hiddenFields={hiddenFields}
              thirdPartyPartnerName={thirdPartyPartnerName}
              availableLocations={availableLocations}
              nativeFields={createIdbNativeFields}
              createValidationSchema={createIdbValidationSchema}
              getCustomFieldOverride={getCustomFieldOverride}
              actions={({ dirty, isValid, submitForm, status }) => {
                return (
                  <>
                    <Button
                      disabled={saving}
                      color="secondary"
                      variant="contained"
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={saving}
                      disabled={!dirty || !isValid || !!status}
                      onClick={submitForm}
                    >
                      Save
                    </LoadingButton>
                  </>
                );
              }}
              pricing={pricing}
            />
          )}
        </LoadAvailableLocations>
      </Container>
    );
  };

  return withOrganizationPricing({ organizationId: rxForm.organization.id, children });
};

UpdateIdbRxForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  rxForm: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  preference: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  modificationRules: PropTypes.object.isRequired,
  hiddenFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  thirdPartyPartnerName: PropTypes.string,
  afterSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSaveFailure: PropTypes.func.isRequired
};

UpdateIdbRxForm.defaultProps = {
  thirdPartyPartnerName: null
};

export default UpdateIdbRxForm;
